import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import DealersSelectField from 'dealers/forms/DealersSelectField';
import { useDispatch, useSelector } from 'react-redux';
import validationSchema from './DealerSearch.schema';
import {
  FormDatePickerField,
  FormGroupCheckboxField,
  FormInputField,
} from '../../common/components/formFields';
import {
  CLEAR_DEALER_SEARCH_STATE,
  CLEAR_DEALERS_SELECT_SEARCH_STATE,
} from '../DealersReducer';
import ComponentCard from '../../common/components/cards/ComponentCard';
import DealerCitySelectField from '../forms/DealerCitySelectField';
import { capitalizeString } from '../../helpers/capitalizeString';

const initialValues = {
  serial: '',
  tradeName: '',
  mainEmail: '',
  registeredDateFrom: '',
  registeredDateTo: '',
  city: '',
  endingFreeTrialInTwoWeeks: false,
};

function DealerSearch({ onSearch }) {
  const dispatch = useDispatch();
  const formRef = useRef();
  const previousSearchState = useSelector((state) => state.dealers.get('searchState'));
  const dealersSelectSearchState = useSelector((state) =>
    state.dealers.get('dealersSelectSearchState'),
  );
  const [reloadDatePickerKey, setReloadDatePickerKey] = useState(false);

  useEffect(() => {
    if (previousSearchState?.searchParams) {
      const dealerLabel = previousSearchState?.searchParams?.tradeName;
      const city = previousSearchState?.searchParams?.city;
      const endingFreeTrialInTwoWeeks =
        previousSearchState?.searchParams?.endingFreeTrialInTwoWeeks;
      if (dealersSelectSearchState) {
        const dealer = dealersSelectSearchState?.filter((it) => it.label === dealerLabel)[0];
        formRef.current.setValues({
          ...previousSearchState?.searchParams,
          tradeName: dealer,
        });
      } else {
        formRef.current.setValues(previousSearchState?.searchParams);
      }
      if (city) {
        formRef.current.setValues({
          ...previousSearchState?.searchParams,
          city: { value: city, label: capitalizeString(city) },
        });
      }
      if (endingFreeTrialInTwoWeeks) {
        formRef.current.setValues({
          ...previousSearchState?.searchParams,
          endingFreeTrialInTwoWeeks: [endingFreeTrialInTwoWeeks],
        });
      }
    }
  }, [previousSearchState]);

  const handleClear = (resetForm, submitForm) => {
    dispatch({
      type: CLEAR_DEALER_SEARCH_STATE,
    });
    dispatch({
      type: CLEAR_DEALERS_SELECT_SEARCH_STATE,
    });
    setReloadDatePickerKey((prevState) => !prevState);
    resetForm();
    submitForm();
  };

  return (
    <Row>
      <Col md="12">
        <ComponentCard title="Search Dealer" isCollapsible>
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSearch}
            validateOnBlur
          >
            {({ resetForm, submitForm }) => (
              <Form noValidate>
                <Row>
                  <Col md="4">
                    <FormInputField name="serial" label="ID" defaultValue="" />
                  </Col>
                  <Col md="4">
                    <DealersSelectField name="tradeName" label="Dealer" />
                  </Col>
                  <Col md="4">
                    <FormInputField name="mainEmail" label="Email" />
                  </Col>
                </Row>
                <Row>
                  <Col md="4">
                    <FormDatePickerField
                      name="registeredDateFrom"
                      label="Registered Date From"
                      key={reloadDatePickerKey}
                    />
                  </Col>
                  <Col md="4">
                    <FormDatePickerField
                      name="registeredDateTo"
                      label="Registered Date To"
                      key={reloadDatePickerKey}
                      isUpperRange
                    />
                  </Col>
                  <Col md="4">
                    <DealerCitySelectField label="City" name="city" />
                  </Col>
                </Row>
                <Row>
                  <Col md="4" className="mb-3">
                    <FormGroupCheckboxField
                      inline
                      name="endingFreeTrialInTwoWeeks"
                      checkboxes={[
                        {
                          value: true,
                          label: 'Free Trial Ends In 2 Weeks',
                        },
                      ]}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <Button
                      className="mt-4 float-end ms-2 rounded-pill btn-primary"
                      type="submit"
                    >
                      Search
                    </Button>
                    <Button
                      className="mt-4 float-end rounded-pill btn-soft-primary"
                      type="button"
                      onClick={() => handleClear(resetForm, submitForm)}
                    >
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </ComponentCard>
      </Col>
    </Row>
  );
}

DealerSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

export default DealerSearch;
